import React, { useState } from "react"

import { Form, Formik } from "formik"
import * as Yup from "yup"

import { gql } from "@urql/core"

import airbrake from "src/airbrake-notifier"
import { countryOptions, regionOptions } from "src/enums"
import readFile from "src/readFile"

import { useMutation } from "hooks/urql"

import {
  CheckboxField,
  Field,
  FileField,
  FormStatus,
  SelectField,
  SubmitButton,
  TextAreaField,
  TimeZoneField,
} from "components/Forms/Formik/hookComponents"

const ValidationSchema = Yup.object().shape({
  email: Yup.string().required(),
})

const TutorForm = ({ tutor, tiers, canAdminExpenses, canAdminBudgets }) => {
  const formState = {
    id: tutor?.id,
    firstName: tutor?.firstName || "",
    lastName: tutor?.lastName || "",
    email: tutor?.email || "",
    phoneNumber: tutor?.phoneNumber || "",
    bio: tutor?.bio || "",
    emailTimeZone: tutor?.emailTimeZone || "Pacific",
    country: tutor?.address?.country || "United States",
    region: tutor?.address?.region || "California",
    onDemandTutor: tutor?.onDemandTutor || false,
    avatar: {},
    disableTutorNotifications: tutor?.disableTutorNotifications || false,
    messageEmailsEnabled: tutor?.messageEmailsEnabled || false,
    activeCourseStudyGroupLimit: tutor?.activeCourseStudyGroupLimit || 20,
    tierIds: tutor?.tiers?.map(tier => tier.id) || [],
    canSubmitGeneralExpenses: tutor?.canSubmitGeneralExpenses || false,
    hasSubmittedFingerprintExpense:
      tutor?.hasSubmittedFingerprintExpense || false,
    hasSubmittedIntroSessionExpense:
      tutor?.hasSubmittedIntroSessionExpense || false,
    billableName: tutor?.billableName || "",
    floridaTutoringAdvantageId: tutor?.floridaTutoringAdvantageId || "",
    messagingDisabled: tutor?.messagingDisabled || false,
  }
  const [errors, setErrors] = useState([])
  const [, saveTutor] = useMutation(saveTutorMutation)

  const handleSubmit = (values, actions) => {
    readFile(values.avatar)
      .then(file => {
        values.avatar = file
        saveTutor({ input: values })
          .then(response => {
            const { failures, redirectTo } = response.data.tutorSave
            if (failures) {
              setErrors(failures)
            } else {
              window.location.href = redirectTo
            }
          })
          .catch(error => {
            setErrors([{ message: "Something went wrong" }])
            airbrake.notify({
              error,
              name: "AdminTutorForm#saveTutor",
            })
          })
          .finally(() => actions.setSubmitting(false))
      })
      .catch(error => {
        setErrors([{ message: "Something went wrong" }])
        airbrake.notify({
          error,
          name: "AdminTutorForm#readFile",
        })
        actions.setSubmitting(false)
      })
  }

  return (
    <div className="w-full md:w-2/3 lg:w-1/2">
      <Formik
        initialValues={formState}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
      >
        <Form>
          <Field name="firstName" label="First Name" />
          <Field name="lastName" label="Last Name" />
          {canAdminBudgets && (
            <Field
              name="billableName"
              label="Billable Name (their business name as it appears in Bill.com)"
            />
          )}
          <Field name="email" label="Email" />
          <Field name="phoneNumber" label="Phone Number" />
          <TextAreaField name="bio" label="Bio" />
          <TimeZoneField name="emailTimeZone" label="Time Zone" />
          <SelectField
            name="country"
            label="Country"
            options={countryOptions}
            defaultValue={countryOptions.find(
              o => o.value === formState.country
            )}
          />
          <SelectField
            name="region"
            label="Region"
            options={regionOptions.filter(r => r.value !== "Other")}
            defaultValue={regionOptions.find(o => o.value === formState.region)}
          />
          <CheckboxField name="onDemandTutor" label="On-Demand Tutor?" />
          <FileField name="avatar" label="Avatar" accept="image/*" />
          <CheckboxField
            name="messagingDisabled"
            label="Messaging Feature Disabled"
          />
          <CheckboxField
            name="disableTutorNotifications"
            label="Disable Tutor Notifications"
          />
          <CheckboxField
            name="messageEmailsEnabled"
            label="Message Emails Enabled"
          />
          <Field
            type="number"
            name="activeCourseStudyGroupLimit"
            label="Active Course Study Group Limit"
          />
          <Field
            name="floridaTutoringAdvantageId"
            label="Florida Tutoring Advantage Id"
          />
          <label>Grade Ranges</label>

          <CheckboxField
            isMulti
            name="tierIds"
            options={tiers.map(tier => ({
              value: tier.id,
              label: tier.gradeRange,
            }))}
          />

          {canAdminExpenses && (
            <>
              {tutor && !tutor.billDotComVendorId ? (
                <p className="alert alert-info">
                  This tutor does not have a linked Bill.com account, so we
                  cannot enable the expense system for them at this time. Please
                  reach out to development to have the tutors vendor ID set if
                  you need to enable the expense system for them.
                </p>
              ) : (
                <>
                  <label>Expenses</label>
                  <CheckboxField
                    name="canSubmitGeneralExpenses"
                    label="Can Submit General Expenses"
                  />
                  <CheckboxField
                    name="hasSubmittedFingerprintExpense"
                    label="Has Submitted Fingerprint Expense"
                  />
                  <CheckboxField
                    name="hasSubmittedIntroSessionExpense"
                    label="Has Submitted Intro Session Expense"
                  />
                </>
              )}
            </>
          )}

          {errors.map((error, index) => (
            <div key={index} className="alert alert-danger">
              {error.message}
            </div>
          ))}
          <FormStatus />
          <SubmitButton />
        </Form>
      </Formik>
    </div>
  )
}

const saveTutorMutation = gql`
  mutation TutorSave($input: TutorInputObject!) {
    tutorSave(input: $input) {
      redirectTo
      failures {
        message
      }
    }
  }
`

export default TutorForm
